import * as React from "react";
import { RotateCcw } from "lucide-react";
import { Button } from "randmarcomps";
import InlineSpinner from "./InlineSpinner";

interface DataRefreshBarProps {
  lastRefreshedAt?: Date | null;
  isRefreshing?: boolean;
  onRefresh?: () => unknown;
  className?: string;
  align?: "between" | "right";
  actions?: React.ReactNode;
}

const formatRefreshTimestamp = (timestamp: Date) =>
  new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(timestamp);

export function DataRefreshBar({
  lastRefreshedAt,
  isRefreshing,
  onRefresh,
  className,
  align = "between",
  actions,
}: DataRefreshBarProps) {
  const [isManualRefreshRunning, setIsManualRefreshRunning] = React.useState(false);
  const isBusy = Boolean(isRefreshing || isManualRefreshRunning);

  const handleRefresh = React.useCallback(async () => {
    if (!onRefresh || isBusy) {
      return;
    }

    setIsManualRefreshRunning(true);
    try {
      await onRefresh();
    } finally {
      setIsManualRefreshRunning(false);
    }
  }, [isBusy, onRefresh]);

  const statusLabel = lastRefreshedAt ? `Last refreshed ${formatRefreshTimestamp(lastRefreshedAt)}` : "Not refreshed yet";
  const containerClassName = align === "right" ? "justify-end" : "justify-between";

  return (
    <div className={["flex flex-wrap items-center gap-3", containerClassName, className].filter(Boolean).join(" ")}>
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500" role="status" aria-live="polite">
        {isBusy ? "Refreshing…" : statusLabel}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={!onRefresh || isBusy}
          className="flex items-center gap-2 rounded-xl"
        >
          {isBusy ? (
            <InlineSpinner size="sm" decorative />
          ) : (
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
          )}
          Refresh
        </Button>
        {actions}
      </div>
    </div>
  );
}
